import React, { useState } from 'react';
import { Button } from '@savvy-kit/components/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@savvy-kit/components/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@savvy-kit/components/select';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import type { MarketingRequest } from './marketing-requests-list';
import { MarketingRequestRow } from './marketing-request-row';

const MARKETING_ROWS_PER_PAGE_SELECT_ITEMS = [
  { value: '5', label: '5' },
  { value: '10', label: '10' },
  { value: '25', label: '25' },
];

export function MarketingRequestDialog({
  open,
  onClose,
  requests,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly requests: MarketingRequest[];
}) {
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(1);

  // Reset page number when dialog opens or when requests change
  React.useEffect(() => {
    if (open) {
      setPage(1);
    }
  }, [open, requests.length]);

  const totalPages = Math.max(1, Math.ceil(requests.length / rowsPerPage));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, requests.length);
  const paginatedRequests = requests.slice(startIndex, endIndex);

  const handlePageChange = (newPage: number) => {
    setPage(Math.min(Math.max(1, newPage), totalPages));
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[800px] p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>Completed Requests</DialogTitle>
        </DialogHeader>
        <div className="max-h-[600px] overflow-y-auto border-t border-light">
          {requests.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-secondary">No completed requests yet</p>
            </div>
          ) : (
            <div className="divide-y divide-border-light">
              {paginatedRequests.map((request: MarketingRequest) => (
                <MarketingRequestRow key={request.issueId} request={request} />
              ))}
            </div>
          )}
        </div>
        {requests.length > 0 && (
          <div className="flex items-center justify-end gap-4 px-4 py-3 border-t border-light">
            <div className="flex items-center gap-2">
              <span className="text-sm text-secondary">Rows per page:</span>
              <Select
                value={String(rowsPerPage)}
                items={MARKETING_ROWS_PER_PAGE_SELECT_ITEMS}
                onValueChange={(value) => {
                  setRowsPerPage(Number(value));
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[70px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MARKETING_ROWS_PER_PAGE_SELECT_ITEMS.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon-xs"
                className="size-8"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="size-4" />
              </Button>
              <span className="text-sm">
                {currentPage} of {totalPages}
              </span>
              <Button
                variant="ghost"
                size="icon-xs"
                className="size-8"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
